import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Mail, Phone, MapPin, Send } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Pluck a localized string for the active language, falling back to the English source field.
const tField = (data, lang, key) => {
  const tr = (data?.translations || {})[lang];
  if (tr && tr[key] != null && String(tr[key]).trim()) return tr[key];
  return data?.[key] ?? '';
};

const Contact = () => {
  const { i18n } = useTranslation();
  const lang = i18n.language?.split('-')[0] || 'en';
  const [data, setData] = useState(null);

  useEffect(() => {
    axios.get(`${API}/contact`)
      .then((res) => setData(res.data))
      .catch(() => {/* will fall back to defaults below */});
  }, []);

  // Allow rendering even before the network call completes — the backend's defaults
  // mirror the original hard-coded copy so first-paint is graceful.
  const c = data || {};
  const phones = c.phones || [];
  const heroTitle = useMemo(() => tField(c, lang, 'hero_title'), [c, lang]);
  const heroSubtitle = useMemo(() => tField(c, lang, 'hero_subtitle'), [c, lang]);
  const emailResponseTime = useMemo(() => tField(c, lang, 'email_response_time'), [c, lang]);
  const addressLabel = useMemo(() => tField(c, lang, 'address_label'), [c, lang]);
  const address = useMemo(() => tField(c, lang, 'address'), [c, lang]);
  const addressNote = useMemo(() => tField(c, lang, 'address_note'), [c, lang]);
  const ctaTitle = useMemo(() => tField(c, lang, 'cta_title'), [c, lang]);
  const ctaText = useMemo(() => tField(c, lang, 'cta_text'), [c, lang]);
  const ctaButtonText = useMemo(() => tField(c, lang, 'cta_button_text'), [c, lang]);

  // Strip any non-dialing characters when building the tel: link
  const telHref = (num) => `tel:${(num || '').replace(/[^+\d]/g, '')}`;

  return (
    <div className="py-16" data-testid="contact-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="text-5xl sm:text-6xl font-heading font-bold text-maroon mb-6" data-testid="contact-hero-title">
            {heroTitle}
          </h1>
          <p className="text-xl text-maroon/70 max-w-3xl mx-auto" data-testid="contact-hero-subtitle">
            {heroSubtitle}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
          {/* Phones — render the first one in the main card; extras stacked below */}
          <div className="card text-center" data-testid="contact-phones-card">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-terracotta/10 mb-4">
              <Phone size={28} className="text-terracotta" />
            </div>
            {phones.length === 0 ? (
              <>
                <h3 className="font-heading font-bold text-xl text-maroon mb-2">Call Us</h3>
                <p className="text-sm text-maroon/60">No phone listed</p>
              </>
            ) : (
              <div className="space-y-4">
                {phones.map((p, idx) => (
                  <div key={idx} data-testid={`contact-phone-row-${idx}`}>
                    <h3 className="font-heading font-bold text-xl text-maroon mb-2">{p.label || 'Call Us'}</h3>
                    <a href={telHref(p.number)} className="text-maroon/70 hover:text-terracotta underline font-semibold">
                      {p.number}
                    </a>
                    {p.hours && <p className="text-sm text-maroon/60 mt-2">{p.hours}</p>}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Email */}
          <div className="card text-center" data-testid="contact-email-card">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-terracotta/10 mb-4">
              <Mail size={28} className="text-terracotta" />
            </div>
            <h3 className="font-heading font-bold text-xl text-maroon mb-2">Email Us</h3>
            {c.email ? (
              <a href={`mailto:${c.email}`} className="text-maroon/70 hover:text-terracotta underline">
                {c.email}
              </a>
            ) : (
              <p className="text-sm text-maroon/60">No email listed</p>
            )}
            {emailResponseTime && <p className="text-sm text-maroon/60 mt-2">{emailResponseTime}</p>}
          </div>

          {/* Address */}
          <div className="card text-center" data-testid="contact-address-card">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-terracotta/10 mb-4">
              <MapPin size={28} className="text-terracotta" />
            </div>
            <h3 className="font-heading font-bold text-xl text-maroon mb-2">{addressLabel || 'Visit Us'}</h3>
            {address ? (
              <p className="text-maroon/70 whitespace-pre-line">{address}</p>
            ) : (
              <p className="text-maroon/70">{addressNote || 'Coming Soon'}</p>
            )}
          </div>
        </div>

        {/* Map */}
        {c.map_embed_url && (
          <div className="mb-12 rounded-sm overflow-hidden border-2 border-maroon/15 shadow-sm" data-testid="contact-map-iframe-wrapper">
            <iframe
              src={c.map_embed_url}
              title="Office location map"
              className="w-full h-96"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
              data-testid="contact-map-iframe"
            />
          </div>
        )}

        {/* CTA */}
        <div className="bg-maroon text-cream p-12 rounded-sm" data-testid="contact-cta">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-3xl font-heading font-bold mb-4">
              {ctaTitle}
            </h2>
            <p className="text-cream/90 mb-8">
              {ctaText}
            </p>
            <a
              href={c.cta_button_link || '/consultation'}
              className="inline-flex items-center space-x-2 bg-terracotta text-white hover:bg-terracotta/90 hover:translate-y-[-2px] transition-all duration-200 font-bold tracking-wide uppercase px-8 py-4 rounded-sm shadow-lg"
              data-testid="contact-cta-button"
            >
              <span>{ctaButtonText}</span>
              <Send size={20} />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
